"use client";

import React from "react";
import { Wrench, ShieldCheck, Flame, Gauge, CircleCheck as CheckCircle2, Truck, Award, Sparkles, MapPin, Phone, MessageCircle, Clock } from 'lucide-react';
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
export default function SaudiCeramicOvenMaintenance() {
  const serviceCapabilities = [{
    icon: Wrench,
    title: "تشخيص الأعطال بدقة عالية",
    description: "فحص شامل للأعطال الكهربائية ومسارات الغاز المخصصة لأفران الخزف السعودي مع معالجة فورية للمشاكل الفنية داخل المنزل."
  }, {
    icon: ShieldCheck,
    title: "قطع غيار الخزف السعودي الأصلية",
    description: "توفير واستبدال قطع الغيار الأصلية المعتمدة لضمان كفاءة تشغيلية مطابقة لمواصفات المصنع وأمان تام للاستخدام."
  }, {
    icon: Flame,
    title: "صيانة أنظمة الإشعال الذاتي",
    description: "إصلاح شمعات الشرر، تنظيف مجاري الغاز للعيون والشواية، وضمان اشتعال سلس وفوري بدون تأخير أو تسريب."
  }, {
    icon: Gauge,
    title: "معايرة الثرموستات وحساسات الأمان",
    description: "ضبط درجات حرارة الفرن وفحص صمامات الأمان وقواطع الغاز التلقائية لضمان تسخين متوازن وحماية من الحوادث."
  }];
  const whyChooseUs = [{
    icon: Award,
    title: "خبرة متخصصة بأفران الخزف السعودي",
    desc: "فنيون معتمدون ومدربون على كافة موديلات ومواصفات أجهزة الخزف السعودي القديمة والحديثة."
  }, {
    icon: Truck,
    title: "خدمة منزلية سريعة بالرياض",
    desc: "سيارات صيانة مجهزة تصلك إلى باب المنزل في كافة أحياء الرياض دون الحاجة لنقل الفرن."
  }, {
    icon: CheckCircle2,
    title: "ضمان حقيقي ومعتمد",
    desc: "ضمان كتابي شامل على قطع الغيار المستبدلة وعلى جودة ودقة عملية الصيانة المنفذة."
  }, {
    icon: Sparkles,
    title: "نظافة وأمان مهني بالمنزل",
    desc: "التزام كامل بمعايير السلامة والنظافة أثناء الفحص وإجراء الإصلاحات داخل مطبخ العميل."
  }];
  const coverageAreas = ["شمال الرياض (الملقا، الياسمين، النرجس، الصحافة، العقيق)", "شرق الرياض (الرمال، المونسية، اليرموك، الحمراء، الروضة)", "وسط وغرب الرياض (العليا، السليمانية، السويدي، ظهرة لبن)", "جنوب الرياض (الشفا، العزيزية، نمار، الحزم)"];
  const faqs = [{
    id: "faq-1",
    question: "كم تستغرق زيارة صيانة فرن الخزف السعودي بالمنزل؟",
    answer: "تستغرق الزيارة الفنية في المتوسط بين 45 إلى 90 دقيقة حسب نوع العطل وحاجة الجهاز لاستبدال قطع غيار أو معايرة الحساسات، ويتم إنهاء كافة الأعمال في نفس الزيارة دون نقل الفرن."
  }, {
    id: "faq-2",
    question: "هل تتوفر قطع غيار أصلية خاصة بماركة الخزف السعودي؟",
    answer: "نعم، نحرص في لمسة احتراف على توفير قطع الغيار الأصلية المعتمدة لأفران الخزف السعودي بما في ذلك شمعات الإشعال، مفاتيح التحكم، حساسات الأمان، والمقابض والمفصلات الحرارية."
  }, {
    id: "faq-3",
    question: "ما هي شروط وتفاصيل ضمان الصيانة المقدم؟",
    answer: "نقدم ضماناً رسمياً معتمداً يشمل القطع المستبدلة وعمليات الإصلاح المنفذة، ويتضمن المتابعة المجانية في حال حدوث أي ملاحظة خلال فترة الضمان المحددة في الفاتورة."
  }, {
    id: "faq-4",
    question: "كيف يمكنني حجز موعد زيارة الفني في الرياض؟",
    answer: `يمكنك حجز الموعد مباشرة عبر الاتصال الهاتفي السريع برقم ${PHONE_LOCAL} أو بالنقر على زر المراسلة الفورية عبر واتساب لتحديد الحي والوقت الأنسب لزيارة فريقنا الفني.`
  }];
  return <section id="saudiceramicovenmaintenance" data-nav="light" dir="rtl" className="bg-background text-foreground py-16 md:py-24 border-b border-border selection:bg-primary/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Block 1: Header & Service Capabilities */}
        <div className="text-right max-w-3xl mb-14">
          <div className="w-8 h-1 bg-primary rounded-full mb-4" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-sans mb-4">
            صيانة أفران الخزف السعودي بالرياض
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            خدمة منزلية متخصصة في الكشف والمعالجة الدقيقة لأعطال أفران الخزف السعودي الغازية والكهربائية. نضمن لك عودة كفاءة جهازك بأعلى معايير الأمان والسلامة.
          </p>
        </div>

        {/* 4-column Technical Capabilities Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {serviceCapabilities.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={index} data-index={index} initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.45,
            delay: index * 0.08,
            ease: "easeOut"
          }}>
                <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-shadow duration-200 text-right">
                  <CardHeader className="gap-3">
                    <div className="size-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Block 2: Why Choose Us */}
        <div className="mb-20">
          <div className="mb-8 text-right">
            <Badge variant="outline" className="mb-2 border-primary/30 text-primary">
              معايير الجودة والاعتماد
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              لماذا تختار لمسة احتراف لصيانة فرن الخزف السعودي؟
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyChooseUs.map((item, index) => {
            const Icon = item.icon;
            return <div key={index} data-index={index} className="p-6 rounded-xl border border-border bg-muted/40 flex items-start gap-4 text-right">
                  <div className="size-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Icon className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Block 3: Service Coverage Strip */}
        <div className="mb-20 p-8 rounded-2xl border border-border bg-accent/40 text-right">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm mb-2">
                <MapPin className="size-4" />
                <span>تغطية شاملة ومباشرة</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                خدمة صيانة منزلية تغطي كافة أحياء الرياض
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                فرقنا الفنية المتنقلة مجهزة بأحدث أدوات القياس وقطع الغيار للوصول إليك فوراً في أي موقع داخل مدينة الرياض وضواحيها.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 max-w-md">
              {coverageAreas.map((area, idx) => <span key={idx} data-index={idx} className="text-xs bg-background border border-border px-3 py-1.5 rounded-md text-foreground font-medium">
                  {area}
                </span>)}
            </div>
          </div>
        </div>

        {/* Block 4: FAQ Accordion */}
        <div className="mb-20 max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-2 border-border text-muted-foreground">
              الأسئلة الشائعة
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              كل ما تود معرفته عن خدمة صيانة أفران الخزف السعودي
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full border border-border rounded-xl px-6 bg-card divide-y divide-border">
            {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-none py-2">
                <AccordionTrigger className="text-base font-semibold text-foreground hover:no-underline text-right py-4 cursor-pointer">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed text-right pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Block 5: Call to Action Booking Banner */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 text-center shadow-lg relative overflow-hidden">
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 bg-secondary-foreground/10 text-secondary-foreground px-3 py-1 rounded-full text-xs font-medium">
              <Clock className="size-3.5" />
              <span>فحص وصيانة منزلية في نفس اليوم</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
              احجز موعد صيانة فرن الخزف السعودي الآن
            </h2>
            
            <p className="text-sm sm:text-base text-secondary-foreground/80 leading-relaxed">
              تواصل مباشرة مع فنيي شركة لمسة احتراف المعتمدين بالرياض واحصل على خدمة سريعة بضمان كامل لكافة قطع الغيار والأعمال المنفذة.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 text-base shadow-md">
                <Link to={WHATSAPP_HREF} newTab className="gap-2">
                  <MessageCircle className="size-5" />
                  <span>حجز عبر واتساب: {PHONE_LOCAL}</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-secondary-foreground/30 bg-secondary-foreground/10 text-secondary-foreground hover:bg-secondary-foreground/20 font-medium px-8 text-base">
                <a href={PHONE_TEL_HREF} className="gap-2">
                  <Phone className="size-4" />
                  <span>اتصال مباشر: {PHONE_LOCAL}</span>
                </a>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}